import { create } from "zustand";
import {
  persist,
  createJSONStorage,
  StateStorage,
} from "zustand/middleware";

interface ViewedGuide {
  guideId: string;
  userId: string;
  viewedAt: number;
}

interface UserActivityState {
  // State
  viewedGuides: ViewedGuide[];

  // Actions
  markGuideAsViewed: (guideId: string, userId: string) => void;
  hasViewedGuide: (guideId: string, userId: string) => boolean;
  clearViewedGuides: () => void;
}

// Create a custom storage object using sessionStorage
const sessionStorageAdapter: StateStorage = {
  getItem: (name) => {
    const str = sessionStorage.getItem(name);
    return str;
  },
  setItem: (name, value) => sessionStorage.setItem(name, value),
  removeItem: (name) => sessionStorage.removeItem(name),
};

// Storage key for sessionStorage
const STORAGE_KEY = "user-activity-viewed-guides";

// Create the store with persistence
export const useUserActivityStore = create<UserActivityState>()(
  persist(
    (set, get) => ({
      // Initial state
      viewedGuides: [],

      // Actions
      markGuideAsViewed: (guideId, userId) =>
        set((state) => {
          // Check if already marked as viewed
          const alreadyViewed = state.viewedGuides.some(
            (vg) => vg.guideId === guideId && vg.userId === userId
          );

          if (alreadyViewed) {
            return state; // No change needed
          }

          return {
            viewedGuides: [
              ...state.viewedGuides,
              { guideId, userId, viewedAt: Date.now() },
            ],
          };
        }),

      hasViewedGuide: (guideId, userId) => {
        const state = get();
        return state.viewedGuides.some(
          (vg) => vg.guideId === guideId && vg.userId === userId
        );
      },

      clearViewedGuides: () => set({ viewedGuides: [] }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorageAdapter),
    }
  )
);
